import { useState, useRef, useEffect } from 'react';
import { Button } from '../../button';
import { MoveIcon, LoaderCircleIcon } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from '../../dialog';

interface CropArea {
	x: number;
	y: number;
	width: number;
	height: number;
}

type ResizeHandle =
	| 'top-left'
	| 'top-right'
	| 'bottom-left'
	| 'bottom-right'
	| 'top'
	| 'right'
	| 'bottom'
	| 'left'
	| 'move'
	| null;

const MAX_FILE_SIZE = 4 * 1024 * 1024; // 4MB in bytes

export function ImageEditor(props: {
	open: boolean;
	file: File | null;
	onSave: (file: File, cropArea: CropArea) => void;
	onOpenChange: (open: boolean) => void;
	aspectRatio?: { width: number; height: number };
	initialCropArea?: CropArea;
}) {
	const [imageSrc, setImageSrc] = useState<string>('');
	const [cropArea, setCropArea] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
	const [isDragging, setIsDragging] = useState(false);
	const [resizeHandle, setResizeHandle] = useState<ResizeHandle>(null);
	const [dragStart, setDragStart] = useState({ x: 0, y: 0 });
	const [initialCropArea, setInitialCropArea] = useState<CropArea>({ x: 0, y: 0, width: 0, height: 0 });
	const [isSaving, setIsSaving] = useState(false);
	const [imageLoaded, setImageLoaded] = useState(false); // ✅ Track when image is rendered
	const [imageRect, setImageRect] = useState<DOMRect | null>(null); // ✅ Track image bounds
	const canvasRef = useRef<HTMLCanvasElement>(null);
	const containerRef = useRef<HTMLDivElement>(null);
	const imageRef = useRef<HTMLImageElement>(null);

	useEffect(() => {
		if (!props.file) return;

		setImageLoaded(false); // ✅ Reset when new file loads
		setImageRect(null); // ✅ Reset image bounds
		const url = URL.createObjectURL(props.file);
		setImageSrc(url);

		const img = new Image();

		const handleLoad = () => {
			// If we have an initial crop area, use it
			if (props.initialCropArea) {
				setCropArea(props.initialCropArea);
				setInitialCropArea(props.initialCropArea); // ✅ Also set the initialCropArea state for dragging
				return;
			}

			// Otherwise, initialize crop area with aspect ratio constraint
			const aspectRatio = props.aspectRatio ? props.aspectRatio.width / props.aspectRatio.height : null;

			let width = img.width;
			let height = img.height;
			let x = 0;
			let y = 0;

			if (aspectRatio) {
				const imageAspectRatio = img.width / img.height;

				if (imageAspectRatio > aspectRatio) {
					// Image is wider than target aspect ratio
					width = img.height * aspectRatio;
					height = img.height;
					x = (img.width - width) / 2;
				} else {
					// Image is taller than target aspect ratio
					width = img.width;
					height = img.width / aspectRatio;
					y = (img.height - height) / 2;
				}
			}

			const defaultCropArea = { x, y, width, height };
			setCropArea(defaultCropArea);
			setInitialCropArea(defaultCropArea); // ✅ Set initial crop area for dragging
		};

		img.addEventListener('load', handleLoad);
		img.src = url;

		return () => {
			URL.revokeObjectURL(url);
			img.removeEventListener('load', handleLoad);
		};
	}, [props.file, props.aspectRatio, props.initialCropArea]);

	// Handle image load event to calculate dimensions after dialog animation
	useEffect(() => {
		if (!imageRef.current || !imageSrc) return;

		const img = imageRef.current;

		const handleImageLoad = () => {
			// Wait for dialog animation to complete before calculating dimensions
			setTimeout(() => {
				setImageLoaded(true);
				if (imageRef.current) {
					setImageRect(imageRef.current.getBoundingClientRect());
				}
			}, 150); // Wait for dialog animation (typically 150ms)
		};

		// If image is already loaded, trigger immediately
		if (img.complete && img.naturalWidth > 0) {
			handleImageLoad();
		} else {
			// Otherwise, wait for load event
			img.addEventListener('load', handleImageLoad);
			return () => img.removeEventListener('load', handleImageLoad);
		}
	}, [imageSrc]);

	// Handle window resize to recalculate image dimensions
	useEffect(() => {
		if (!imageRef.current || !imageLoaded) return;

		const handleResize = () => {
			if (imageRef.current) {
				setImageRect(imageRef.current.getBoundingClientRect());
			}
		};

		window.addEventListener('resize', handleResize);
		return () => window.removeEventListener('resize', handleResize);
	}, [imageLoaded]);

	// Global mouse event handlers for dragging
	useEffect(() => {
		if (!isDragging) return;

		const handleGlobalMouseMove = (e: MouseEvent) => {
			if (!imageRef.current) return;

			const img = imageRef.current;
			const imgRect = img.getBoundingClientRect();
			const scaleX = img.naturalWidth / imgRect.width;
			const scaleY = img.naturalHeight / imgRect.height;

			const deltaX = (e.clientX - dragStart.x) * scaleX;
			const deltaY = (e.clientY - dragStart.y) * scaleY;

			const aspectRatio = props.aspectRatio ? props.aspectRatio.width / props.aspectRatio.height : null;
			const minWidth = props.aspectRatio ? props.aspectRatio.width : 50;
			const minHeight = props.aspectRatio ? props.aspectRatio.height : 50;

			setCropArea(() => {
				const prev = initialCropArea; // Use initial crop area, not previous state
				let newArea = { ...prev };

				// Handle moving the entire crop area
				if (resizeHandle === 'move') {
					newArea.x = Math.max(0, Math.min(prev.x + deltaX, img.naturalWidth - prev.width));
					newArea.y = Math.max(0, Math.min(prev.y + deltaY, img.naturalHeight - prev.height));
					return newArea;
				}

				if (!aspectRatio) {
					// Original free-form resize logic
					switch (resizeHandle) {
						case 'top-left':
							newArea.x = Math.max(0, Math.min(prev.x + deltaX, prev.x + prev.width - minWidth));
							newArea.y = Math.max(0, Math.min(prev.y + deltaY, prev.y + prev.height - minHeight));
							newArea.width = prev.width - (newArea.x - prev.x);
							newArea.height = prev.height - (newArea.y - prev.y);
							break;
						case 'top-right':
							newArea.y = Math.max(0, Math.min(prev.y + deltaY, prev.y + prev.height - minHeight));
							newArea.width = Math.max(minWidth, Math.min(prev.width + deltaX, img.naturalWidth - prev.x));
							newArea.height = prev.height - (newArea.y - prev.y);
							break;
						case 'bottom-left':
							newArea.x = Math.max(0, Math.min(prev.x + deltaX, prev.x + prev.width - minWidth));
							newArea.width = prev.width - (newArea.x - prev.x);
							newArea.height = Math.max(minHeight, Math.min(prev.height + deltaY, img.naturalHeight - prev.y));
							break;
						case 'bottom-right':
							newArea.width = Math.max(minWidth, Math.min(prev.width + deltaX, img.naturalWidth - prev.x));
							newArea.height = Math.max(minHeight, Math.min(prev.height + deltaY, img.naturalHeight - prev.y));
							break;
						case 'top':
							newArea.y = Math.max(0, Math.min(prev.y + deltaY, prev.y + prev.height - minHeight));
							newArea.height = prev.height - (newArea.y - prev.y);
							break;
						case 'bottom':
							newArea.height = Math.max(minHeight, Math.min(prev.height + deltaY, img.naturalHeight - prev.y));
							break;
						case 'left':
							newArea.x = Math.max(0, Math.min(prev.x + deltaX, prev.x + prev.width - minWidth));
							newArea.width = prev.width - (newArea.x - prev.x);
							break;
						case 'right':
							newArea.width = Math.max(minWidth, Math.min(prev.width + deltaX, img.naturalWidth - prev.x));
							break;
					}
				} else {
					// Aspect ratio constrained resize
					switch (resizeHandle) {
						case 'top-left': {
							// Use the larger delta to determine size change
							const widthFromX = prev.width - deltaX;
							const heightFromY = prev.height - deltaY;

							// Choose which dimension to use based on which changed more
							let newWidth: number;
							let newHeight: number;

							if (Math.abs(deltaX) > Math.abs(deltaY)) {
								// X changed more, use width
								newWidth = Math.max(minWidth, widthFromX);
								newHeight = newWidth / aspectRatio;
							} else {
								// Y changed more, use height
								newHeight = Math.max(minHeight, heightFromY);
								newWidth = newHeight * aspectRatio;
							}

							// Calculate new position (opposite corner stays fixed)
							newArea.x = prev.x + prev.width - newWidth;
							newArea.y = prev.y + prev.height - newHeight;

							// Ensure within bounds
							if (newArea.x < 0) {
								newArea.x = 0;
								newWidth = prev.x + prev.width;
								newHeight = newWidth / aspectRatio;
								newArea.y = prev.y + prev.height - newHeight;
							}
							if (newArea.y < 0) {
								newArea.y = 0;
								newHeight = prev.y + prev.height;
								newWidth = newHeight * aspectRatio;
								newArea.x = prev.x + prev.width - newWidth;
							}

							newArea.width = newWidth;
							newArea.height = newHeight;
							break;
						}
						case 'top-right': {
							// Use the larger delta to determine size change
							const widthFromX = prev.width + deltaX;
							const heightFromY = prev.height - deltaY;

							// Choose which dimension to use based on which changed more
							let newWidth: number;
							let newHeight: number;

							if (Math.abs(deltaX) > Math.abs(deltaY)) {
								// X changed more, use width
								newWidth = Math.max(minWidth, widthFromX);
								newHeight = newWidth / aspectRatio;
							} else {
								// Y changed more, use height
								newHeight = Math.max(minHeight, heightFromY);
								newWidth = newHeight * aspectRatio;
							}

							// Calculate new position (bottom-left corner stays fixed)
							newArea.y = prev.y + prev.height - newHeight;

							// Ensure within bounds
							if (prev.x + newWidth > img.naturalWidth) {
								newWidth = img.naturalWidth - prev.x;
								newHeight = newWidth / aspectRatio;
								newArea.y = prev.y + prev.height - newHeight;
							}
							if (newArea.y < 0) {
								newArea.y = 0;
								newHeight = prev.y + prev.height;
								newWidth = newHeight * aspectRatio;
							}

							newArea.width = newWidth;
							newArea.height = newHeight;
							break;
						}
						case 'bottom-left': {
							const avgDelta = (-deltaX + deltaY) / 2;
							let newWidth = prev.width + avgDelta;
							let newHeight = newWidth / aspectRatio;

							// Ensure minimum size
							if (newWidth < minWidth || newHeight < minHeight) {
								newWidth = minWidth;
								newHeight = minHeight;
							}

							// Calculate new position
							newArea.x = prev.x + (prev.width - newWidth);

							// Ensure within bounds
							if (newArea.x < 0) {
								newArea.x = 0;
								newWidth = prev.x + prev.width;
								newHeight = newWidth / aspectRatio;
							}
							if (newArea.y + newHeight > img.naturalHeight) {
								newHeight = img.naturalHeight - newArea.y;
								newWidth = newHeight * aspectRatio;
								newArea.x = prev.x + prev.width - newWidth;
							}

							newArea.width = newWidth;
							newArea.height = newHeight;
							break;
						}
						case 'bottom-right': {
							const avgDelta = (deltaX + deltaY) / 2;
							let newWidth = prev.width + avgDelta;
							let newHeight = newWidth / aspectRatio;

							// Ensure minimum size
							if (newWidth < minWidth || newHeight < minHeight) {
								newWidth = minWidth;
								newHeight = minHeight;
							}

							// Ensure within bounds
							if (newArea.x + newWidth > img.naturalWidth) {
								newWidth = img.naturalWidth - newArea.x;
								newHeight = newWidth / aspectRatio;
							}
							if (newArea.y + newHeight > img.naturalHeight) {
								newHeight = img.naturalHeight - newArea.y;
								newWidth = newHeight * aspectRatio;
							}

							newArea.width = newWidth;
							newArea.height = newHeight;
							break;
						}
						case 'top':
						case 'bottom': {
							// Use height change, calculate width from aspect ratio
							let newHeight = resizeHandle === 'top' ? prev.height - deltaY : prev.height + deltaY;
							let newWidth = newHeight * aspectRatio;

							// Ensure minimum size
							if (newHeight < minHeight) {
								newHeight = minHeight;
								newWidth = minWidth;
							}

							// Ensure it fits within image bounds
							if (resizeHandle === 'top') {
								const potentialY = prev.y + prev.height - newHeight;
								if (potentialY < 0) {
									newArea.y = 0;
									newHeight = prev.y + prev.height;
									newWidth = newHeight * aspectRatio;
								} else {
									newArea.y = potentialY;
								}
							} else {
								if (newArea.y + newHeight > img.naturalHeight) {
									newHeight = img.naturalHeight - newArea.y;
									newWidth = newHeight * aspectRatio;
								}
							}

							// Center horizontally
							const widthDiff = newWidth - prev.width;
							let newX = prev.x - widthDiff / 2;

							// Adjust if out of bounds
							if (newX < 0) {
								newX = 0;
								newWidth = Math.min(newWidth, img.naturalWidth);
								newHeight = newWidth / aspectRatio;
								if (resizeHandle === 'top') {
									newArea.y = prev.y + prev.height - newHeight;
								}
							} else if (newX + newWidth > img.naturalWidth) {
								newX = img.naturalWidth - newWidth;
							}

							newArea.x = newX;
							newArea.width = newWidth;
							newArea.height = newHeight;
							break;
						}
						case 'left':
						case 'right': {
							// Use width change, calculate height from aspect ratio
							let newWidth = resizeHandle === 'left' ? prev.width - deltaX : prev.width + deltaX;
							let newHeight = newWidth / aspectRatio;

							// Ensure minimum size
							if (newWidth < minWidth) {
								newWidth = minWidth;
								newHeight = minHeight;
							}

							// Ensure it fits within image bounds
							if (resizeHandle === 'left') {
								const potentialX = prev.x + prev.width - newWidth;
								if (potentialX < 0) {
									newArea.x = 0;
									newWidth = prev.x + prev.width;
									newHeight = newWidth / aspectRatio;
								} else {
									newArea.x = potentialX;
								}
							} else {
								if (newArea.x + newWidth > img.naturalWidth) {
									newWidth = img.naturalWidth - newArea.x;
									newHeight = newWidth / aspectRatio;
								}
							}

							// Center vertically
							const heightDiff = newHeight - prev.height;
							let newY = prev.y - heightDiff / 2;

							// Adjust if out of bounds
							if (newY < 0) {
								newY = 0;
								newHeight = Math.min(newHeight, img.naturalHeight);
								newWidth = newHeight * aspectRatio;
								if (resizeHandle === 'left') {
									newArea.x = prev.x + prev.width - newWidth;
								}
							} else if (newY + newHeight > img.naturalHeight) {
								newY = img.naturalHeight - newHeight;
							}

							newArea.y = newY;
							newArea.width = newWidth;
							newArea.height = newHeight;
							break;
						}
					}
				}

				return newArea;
			});
		};

		const handleGlobalMouseUp = () => {
			setIsDragging(false);
			setResizeHandle(null);
		};

		document.addEventListener('mousemove', handleGlobalMouseMove);
		document.addEventListener('mouseup', handleGlobalMouseUp);

		return () => {
			document.removeEventListener('mousemove', handleGlobalMouseMove);
			document.removeEventListener('mouseup', handleGlobalMouseUp);
		};
	}, [isDragging, dragStart, resizeHandle, props.aspectRatio, initialCropArea]);

	const handleMouseDown = (e: React.MouseEvent, handle: ResizeHandle) => {
		e.preventDefault();
		e.stopPropagation();
		setResizeHandle(handle);
		setIsDragging(true);
		setDragStart({ x: e.clientX, y: e.clientY });
		setInitialCropArea(cropArea); // Store the crop area at the start of the drag
	};

	const handleSave = async () => {
		if (!props.file) return;

		const originalFile = props.file; // Store file reference to satisfy TypeScript
		setIsSaving(true);

		// Use setTimeout to allow UI to update before heavy processing
		setTimeout(async () => {
			const img = new Image();

			const handleError = () => {
				console.error('Failed to load image for cropping');
				setIsSaving(false);
			};

			const handleLoad = async () => {
				try {
					// Calculate optimal canvas size to reduce processing time
					// For very large images, we can scale down while maintaining aspect ratio
					const MAX_DIMENSION = 4096; // Maximum width or height
					let canvasWidth = cropArea.width;
					let canvasHeight = cropArea.height;

					// Scale down if crop area is too large
					if (canvasWidth > MAX_DIMENSION || canvasHeight > MAX_DIMENSION) {
						const scale = Math.min(MAX_DIMENSION / canvasWidth, MAX_DIMENSION / canvasHeight);
						canvasWidth = Math.floor(canvasWidth * scale);
						canvasHeight = Math.floor(canvasHeight * scale);
					}

					const canvas = document.createElement('canvas');
					canvas.width = canvasWidth;
					canvas.height = canvasHeight;

					const ctx = canvas.getContext('2d', {
						alpha: false, // Disable alpha channel for better performance
						willReadFrequently: false,
					});

					if (!ctx) {
						console.error('Failed to get canvas context');
						setIsSaving(false);
						return;
					}

					// Use high-quality image smoothing
					ctx.imageSmoothingEnabled = true;
					ctx.imageSmoothingQuality = 'high';

					ctx.drawImage(img, cropArea.x, cropArea.y, cropArea.width, cropArea.height, 0, 0, canvasWidth, canvasHeight);

					// Convert to WebP with quality adjustment
					let quality = 0.92;
					const tryConvert = () => {
						canvas.toBlob(
							(blob) => {
								if (!blob) {
									console.error('Failed to create blob from canvas');
									setIsSaving(false);
									return;
								}

								// If file is too large and quality can be reduced, try again
								if (blob.size > MAX_FILE_SIZE && quality > 0.1) {
									quality -= 0.1;
									tryConvert();
									return;
								}

								const file = new File([blob], originalFile.name.replace(/\.[^/.]+$/, '.webp'), {
									type: 'image/webp',
									lastModified: Date.now(),
								});

								props.onSave(file, cropArea);
								setIsSaving(false);
							},
							'image/webp',
							quality,
						);
					};

					tryConvert();
				} catch (error) {
					console.error('Error during image processing:', error);
					setIsSaving(false);
				}
			};

			img.addEventListener('error', handleError);
			img.addEventListener('load', handleLoad);
			img.src = imageSrc;
		}, 50); // Small delay to allow UI to update
	};

	// Calculate scale based on actual displayed image dimensions
	const scaleX = imageRect && imageRef.current ? imageRef.current.naturalWidth / imageRect.width : 1;
	const scaleY = imageRect && imageRef.current ? imageRef.current.naturalHeight / imageRect.height : 1;

	return (
		<Dialog open={props.open} onOpenChange={(open) => !isSaving && props.onOpenChange(open)}>
			<DialogContent
				className="flex h-[98vh] max-h-[98vh] w-[98vw] max-w-[98vw]! flex-col gap-3 p-6"
				showCloseButton={false}
			>
				<DialogHeader className="flex flex-row items-center justify-between space-y-0">
					<div className="flex flex-col gap-1">
						<DialogTitle>Edit Image</DialogTitle>
						<DialogDescription>
							{props.aspectRatio
								? `Drag the corners and edges to crop the image. Aspect ratio is locked to ${props.aspectRatio.width}:${props.aspectRatio.height}. Drag the center to move the crop area.`
								: 'Drag the corners and edges to crop the image. Drag the center to move the crop area.'}
						</DialogDescription>
					</div>
					<div className="flex gap-2">
						<Button
							type="button"
							onClick={() => props.onOpenChange(false)}
							variant="outline"
							size="sm"
							disabled={isSaving}
						>
							Cancel
						</Button>
						<Button type="button" onClick={handleSave} size="sm" disabled={isSaving}>
							{isSaving ? (
								<>
									<LoaderCircleIcon className="size-4 animate-spin" />
									Converting...
								</>
							) : (
								'Save'
							)}
						</Button>
					</div>
				</DialogHeader>

				<div
					ref={containerRef}
					className="relative flex flex-1 items-center justify-center overflow-hidden rounded-lg p-4"
				>
					{imageSrc && (
						<div className="relative">
							<img
								ref={imageRef}
								src={imageSrc}
								alt="Edit"
								className="max-h-[calc(85vh-2rem)] max-w-[calc(95vw-2rem)] object-contain"
								draggable={false}
								onLoad={() => {
									// Wait for dialog animation to complete before calculating dimensions
									setTimeout(() => {
										setImageLoaded(true);
										if (imageRef.current) {
											setImageRect(imageRef.current.getBoundingClientRect());
										}
									}, 150); // Wait for dialog animation (typically 150ms)
								}}
							/>

							{/* Crop overlay */}
							{imageLoaded && imageRect && imageRef.current && (
								<div
									className="absolute border-2 border-white shadow-[0_0_0_2000px_rgba(0,0,0,0.4)]"
									style={{
										left: `${cropArea.x / scaleX}px`,
										top: `${cropArea.y / scaleY}px`,
										width: `${cropArea.width / scaleX}px`,
										height: `${cropArea.height / scaleY}px`,
									}}
								>
									{/* Move handle - center of crop area */}
									<div
										className="bg-primary/10 hover:bg-primary/20 absolute inset-4 flex cursor-move items-center justify-center transition-colors"
										onMouseDown={(e) => handleMouseDown(e, 'move')}
									>
										<MoveIcon className="text-primary size-8 opacity-50" />
									</div>

									{/* Corner handles */}
									<div
										className="bg-primary absolute -top-2 -left-2 size-4 cursor-nwse-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'top-left')}
									/>
									<div
										className="bg-primary absolute -top-2 -right-2 size-4 cursor-nesw-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'top-right')}
									/>
									<div
										className="bg-primary absolute -bottom-2 -left-2 size-4 cursor-nesw-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'bottom-left')}
									/>
									<div
										className="bg-primary absolute -right-2 -bottom-2 size-4 cursor-nwse-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'bottom-right')}
									/>

									{/* Edge handles */}
									<div
										className="bg-primary absolute -top-2 left-1/2 h-4 w-8 -translate-x-1/2 cursor-ns-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'top')}
									/>
									<div
										className="bg-primary absolute top-1/2 -right-2 h-8 w-4 -translate-y-1/2 cursor-ew-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'right')}
									/>
									<div
										className="bg-primary absolute -bottom-2 left-1/2 h-4 w-8 -translate-x-1/2 cursor-ns-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'bottom')}
									/>
									<div
										className="bg-primary absolute top-1/2 -left-2 h-8 w-4 -translate-y-1/2 cursor-ew-resize rounded-full"
										onMouseDown={(e) => handleMouseDown(e, 'left')}
									/>
								</div>
							)}
						</div>
					)}
				</div>

				<canvas ref={canvasRef} className="hidden" />
			</DialogContent>
		</Dialog>
	);
}
